// App-shell Clerk wiring (Identity/Billing roadmap — closes the staging-cutover gap).
//
// The landing page loads clerk-js only for its sign-in modal. Inside /app, nothing was keeping the
// Clerk session alive, so its short-lived session token went stale and the first authed /api call
// 401'd (this is what broke staging import). Loading clerk-js here fixes that: the SDK keeps the
// session refreshed AND mints a fresh token per request, which we attach as a bearer via api.ts.
//
// DORMANT on prod: CLERK_ENABLED is false in a normal build, so esbuild dead-code-eliminates this
// whole module and the app behaves exactly as today (Cloudflare Access at the edge). Only the
// CLERK_BUILD=1 staging build turns it on.

import {
  CLERK_ENABLED,
  CLERK_PUBLISHABLE_KEY,
  CLERK_APPEARANCE,
  CLERK_PROFILE_APPEARANCE,
  loadClerk,
  type ClerkGlobal,
  type ClerkUser,
} from './clerkConfig.js';
import { setAuthTokenProvider } from './api.js';

// The loaded Clerk instance, shared with signOutClerk() so the Sign-out button reuses the SAME
// instance instead of loading a second one. null once resolved if clerk-js failed to load.
let clerkReady: Promise<ClerkGlobal | null> | null = null;

export function initAppClerk(): void {
  if (!CLERK_ENABLED) return;

  // Start loading immediately; fail open (no token → 401) if clerk-js can't load.
  clerkReady = loadClerk(CLERK_PUBLISHABLE_KEY).catch(() => null);

  // Register the token provider SYNCHRONOUSLY so /api calls that fire during startup wait for the
  // token instead of racing ahead unauthenticated. getToken() returns a fresh, unexpired token when
  // a Clerk session exists, or null when it doesn't.
  setAuthTokenProvider(async () => {
    const clerk = await clerkReady;
    try {
      return (await clerk?.session?.getToken()) ?? null;
    } catch {
      return null;
    }
  });

  // Cutover (2026-08-04): Clerk is now the SOLE gate — Cloudflare Access no longer fronts /app or /api.
  // A direct hit on /app with no Clerk session would load the shell but 401 every /api call, so bounce a
  // session-less visitor to the landing page (which carries sign-in). Fail OPEN if clerk-js didn't load
  // (clerk === null) so a transient SDK failure never traps a signed-in user out of the app.
  void clerkReady.then((clerk) => {
    if (clerk && !clerk.user) window.location.replace('/');
  });
}

// Clerk sign-out for the app shell's Sign-out button (replaces the old Cloudflare Access logout URL,
// which 404s now that Access is off these paths). Ends the Clerk session and returns to the landing
// page. Falls back to a plain navigation if clerk-js never loaded, so the button always leaves the app.
export async function signOutClerk(): Promise<void> {
  const clerk = await (clerkReady ?? Promise.resolve(null));
  try {
    if (clerk?.signOut) {
      await clerk.signOut({ redirectUrl: '/' });
      return;
    }
  } catch {
    // fall through to the hard navigation below
  }
  window.location.assign('/');
}

// ---- Account chip: the signed-in user's name + photo (Identity/Billing roadmap) -----------------
// Read-only view of the Clerk user for the side-nav account chip and the Account view's profile card.
// The actual photo upload / name edit / security live in Clerk's own themed profile modal
// (openAccountProfile), so there is no bespoke upload or storage to build or secure.

export interface ClerkUserInfo {
  firstName: string;
  fullName: string;
  email: string;
  imageUrl: string;
  hasImage: boolean;
}

function toInfo(u: ClerkUser | null | undefined): ClerkUserInfo | null {
  if (!u) return null;
  const email = u.primaryEmailAddress?.emailAddress ?? '';
  const fullName = (u.fullName ?? [u.firstName, u.lastName].filter(Boolean).join(' ')) || '';
  const firstName = u.firstName || fullName.split(' ')[0] || (email ? email.split('@')[0] : '') || '';
  return { firstName, fullName, email, imageUrl: u.imageUrl ?? '', hasImage: Boolean(u.hasImage && u.imageUrl) };
}

/** The ready Clerk instance, or null when Clerk is disabled (non-cutover build) or failed to load. */
async function whenClerk(): Promise<ClerkGlobal | null> {
  return await (clerkReady ?? Promise.resolve(null));
}

/** The current signed-in user, or null. Used by the Account view's profile card. */
export async function getClerkUserInfo(): Promise<ClerkUserInfo | null> {
  const clerk = await whenClerk();
  return toInfo(clerk?.user);
}

/** Call `cb` with the current user (or null) once Clerk is ready, then again whenever it changes
 *  (e.g. after a photo upload), so the chip and the profile card stay in sync. Fails quiet. */
export function onClerkUser(cb: (info: ClerkUserInfo | null) => void): void {
  void whenClerk().then((clerk) => {
    if (!clerk) {
      cb(null);
      return;
    }
    cb(toInfo(clerk.user));
    try {
      clerk.addListener?.(() => cb(toInfo(clerk.user)));
    } catch {
      /* addListener is optional — a one-shot paint is still correct */
    }
  });
}

/** Open Clerk's own account/profile modal, themed (CLERK_APPEARANCE). In the hybrid design we only use
 *  this for the deeper sign-in/security surface (connected accounts, active devices, delete account);
 *  name + photo are edited natively below. No-op if Clerk is unavailable. */
export async function openAccountProfile(): Promise<void> {
  const clerk = await whenClerk();
  try {
    clerk?.openUserProfile?.({ appearance: CLERK_APPEARANCE });
  } catch {
    /* no-op if the profile UI can not open */
  }
}

/** Render Clerk's full UserProfile (name, photo, email addresses, connected accounts, active devices,
 *  delete account) INLINE into `node`, themed to match — so those fields live inside our Account
 *  section instead of a popup. No-op if Clerk is unavailable. */
export async function mountAccountProfile(node: HTMLElement): Promise<void> {
  const clerk = await whenClerk();
  try {
    clerk?.mountUserProfile?.(node, { appearance: CLERK_PROFILE_APPEARANCE });
  } catch {
    /* no-op if the profile component can not mount */
  }
}

/** Tear down an inline-mounted UserProfile before we remove its node (avoids a leak). */
export async function unmountAccountProfile(node: HTMLElement): Promise<void> {
  const clerk = await whenClerk();
  try {
    clerk?.unmountUserProfile?.(node);
  } catch {
    /* best-effort */
  }
}

// ---- Native profile edits (hybrid: our UI, Clerk's SDK under the hood) --------------------------
// These call clerk-js directly, so the photo/name are managed in our own look & feel with no Clerk
// modal. A successful call updates the Clerk user, which fires addListener → onClerkUser, so the
// side-nav chip re-paints on its own; the Account card re-reads getClerkUserInfo() after the call.

/** Upload a new avatar from a picked file. Returns false (never throws) on failure. */
export async function uploadProfileImage(file: File): Promise<boolean> {
  const clerk = await whenClerk();
  try {
    await clerk?.user?.setProfileImage?.({ file });
    return true;
  } catch {
    return false;
  }
}

/** Remove the uploaded avatar (reverts to the Google photo, or the initials monogram). */
export async function removeProfileImage(): Promise<boolean> {
  const clerk = await whenClerk();
  try {
    await clerk?.user?.setProfileImage?.({ file: null });
    return true;
  } catch {
    return false;
  }
}

/** Update the user's display name. `full` is split into first + last on the first space. */
export async function updateName(full: string): Promise<boolean> {
  const clerk = await whenClerk();
  const trimmed = full.trim();
  if (!trimmed) return false;
  const sp = trimmed.indexOf(' ');
  const firstName = sp === -1 ? trimmed : trimmed.slice(0, sp);
  const lastName = sp === -1 ? '' : trimmed.slice(sp + 1).trim();
  try {
    await clerk?.user?.update?.({ firstName, lastName });
    return true;
  } catch {
    return false;
  }
}

// ---- Native sign-in & security (read + safe actions on Clerk's SDK) -----------------------------
// Rendered in our own look inside the Account editor (no embedded Clerk widget): the connected OAuth
// account, active devices (sign out per device), and account deletion. All defensive — a failed read
// yields empty data so the section degrades gracefully rather than breaking the editor.

export interface SecurityDevice {
  id: string;
  current: boolean;
  title: string;
  detail: string;
}
export interface SecurityInfo {
  connectedLabel: string | null;
  email: string;
  devices: SecurityDevice[];
}

export async function getSecurityInfo(): Promise<SecurityInfo | null> {
  const clerk = await whenClerk();
  const user = clerk?.user;
  if (!user) return null;
  const email = user.primaryEmailAddress?.emailAddress ?? '';
  let connectedLabel: string | null = null;
  const ext = user.externalAccounts?.[0];
  if (ext?.provider) {
    const prov = ext.provider.replace(/^oauth_/, '');
    const name = prov ? prov.charAt(0).toUpperCase() + prov.slice(1) : 'account';
    connectedLabel = ext.emailAddress ? `${name} · ${ext.emailAddress}` : name;
  }
  let devices: SecurityDevice[] = [];
  try {
    const raw = (await user.getSessions?.()) ?? [];
    const currentId = clerk?.session?.id;
    devices = raw.map((s) => {
      const act = s.latestActivity ?? {};
      const title = [act.browserName, act.deviceType].filter(Boolean).join(' · ') || 'Unknown device';
      const detail = [act.city, act.country].filter(Boolean).join(', ');
      return { id: s.id, current: Boolean(currentId && s.id === currentId), title, detail };
    });
  } catch {
    devices = [];
  }
  return { connectedLabel, email, devices };
}

/** Revoke (sign out) one device/session. */
export async function signOutDevice(id: string): Promise<boolean> {
  const clerk = await whenClerk();
  try {
    const raw = (await clerk?.user?.getSessions?.()) ?? [];
    const target = raw.find((x) => x.id === id);
    await target?.revoke?.();
    return true;
  } catch {
    return false;
  }
}

/** Permanently delete the Clerk user. Caller must confirm first — this is irreversible. */
export async function deleteAccount(): Promise<boolean> {
  const clerk = await whenClerk();
  try {
    await clerk?.user?.delete?.();
    return true;
  } catch {
    return false;
  }
}

/** The first letter of the user's first name (falling back to their name/email). A middot placeholder
 *  shows only pre-load, before Clerk has resolved the user. */
export function initialsOf(info: ClerkUserInfo | null): string {
  const src = (info?.firstName || info?.fullName || info?.email || '').trim();
  return src ? (src[0] ?? '·').toUpperCase() : '·';
}

/** Paint an avatar host element: the Clerk/Google photo when present, else an initials monogram.
 *  Falls back to initials if the image 404s. All DOM (no innerHTML), so it is CSP-safe. */
export function applyAvatar(el: HTMLElement, info: ClerkUserInfo | null): void {
  const initials = initialsOf(info);
  el.textContent = initials;
  el.setAttribute('data-initials', initials);
  if (info?.hasImage && info.imageUrl) {
    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    img.referrerPolicy = 'no-referrer';
    img.addEventListener(
      'load',
      () => {
        el.textContent = '';
        el.appendChild(img);
      },
      { once: true },
    );
    img.addEventListener('error', () => img.remove(), { once: true }); // keep the initials already shown
    img.src = info.imageUrl;
  }
}
